import { SALES_HUB_ENTERPRISE, SALES_HUB_PROFESSIONAL, SERVICE_HUB_ENTERPRISE } from 'self-service-api/constants/ApiNames';
import { PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import { SERVICE_HUB_PROFESSIONAL } from 'self-service-api/constants/ProductNames';
import { SeatType } from 'ui-addon-upgrades/_core/common/api/fetchAvailableSeats';
import { getHighestProductLevelFromProductInventory, getProductVersionsFromProductInventory } from './productInventoryUtils';
import { CoreSeatApiNameByPaidProductLevel, getCurrentSkuIdForProduct, getQuantityPackForProductVersion, skuIdHasBundledProducts } from './productsUtils';
import { SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { USER } from 'self-service-api/constants/UnitTypes';
export const PricingPageSeatTypes = {
  [SeatType.CORE]: SeatType.CORE,
  // sales seats
  [SeatType.SALES_PRO]: SeatType.SALES_PRO,
  [SeatType.SALES_ENTERPRISE]: SeatType.SALES_ENTERPRISE,
  // service seats
  [SeatType.SERVICE_PROFESSIONAL]: SeatType.SERVICE_PROFESSIONAL,
  [SeatType.SERVICE_ENTERPRISE]: SeatType.SERVICE_ENTERPRISE
};
export const getPaidSeats = ({
  seatsInventory
}) => {
  const paidSeats = seatsInventory.paidSeats;
  return paidSeats || [];
};
export const getTotalNumberOfAccountUsers = ({
  seatsInventory
}) => {
  const paidSeats = getPaidSeats({
    seatsInventory
  });
  if (!paidSeats.length) return 0;
  const total = paidSeats.reduce((acc, paidSeat) => acc + paidSeat.currentAssignedSeats, 0);
  return total;
};

/**
 * @returns A count of all pricing page seats
 */
export const getTotalHubSeatCount = ({
  seatsInventory
}) => {
  const paidSeats = getPaidSeats({
    seatsInventory
  });
  if (!paidSeats.length) return 0;
  return paidSeats.reduce((acc, current) => {
    return acc + (current.maxAssignableSeats || 0);
  }, 0);
};

/**
 * @returns the total number of seats for the specified seat type
 */
export const getTotalHubSeatsCountForSeatType = ({
  seatsInventory
}, seatType) => {
  const paidSeats = getPaidSeats({
    seatsInventory
  });
  if (!paidSeats.length) return 0;
  const seatsInfo = paidSeats.find(item => {
    return item.seatName === seatType;
  });
  return seatsInfo && seatsInfo.maxAssignableSeats || 0;
};

/**
 * @returns the number of un-assigned/avaialable seats for the specified seat type
 */
export const getUnassignedSeatsCountForSeatType = ({
  seatsInventory
}, seatType) => {
  const paidSeats = getPaidSeats({
    seatsInventory
  });
  if (!paidSeats.length) return 0;
  const seatsInfo = paidSeats.find(item => {
    return item.seatName === seatType;
  });
  return seatsInfo && seatsInfo.availableSeatsCount || 0;
};

/**
 * @returns the number of currently assigned seats for the specified seat type
 */
export const getAssignedSeatsCountForSeatType = ({
  seatsInventory
}, seatType) => {
  const paidSeats = getPaidSeats({
    seatsInventory
  });
  if (!paidSeats.length) return 0;
  const seatsInfo = paidSeats.find(item => {
    return item.seatName === seatType;
  });
  return seatsInfo && seatsInfo.currentAssignedSeats || 0;
};

/**
 * @returns an array of all available seat types
 */
export const getAllHubSeatTypes = ({
  seatsInventory
}) => {
  const paidSeats = getPaidSeats({
    seatsInventory
  });
  if (!paidSeats.length) return [];
  return paidSeats.filter(seat => seat.maxAssignableSeats && seat.maxAssignableSeats > 0).map(seat => seat.seatName);
};

/**
 * @returns an array of all available seat types
 */
export const getAllPurchasableHubSeatTypes = ({
  seatsInventory
}) => {
  const paidSeats = getPaidSeats({
    seatsInventory
  });
  if (!paidSeats.length) return [];
  return paidSeats.filter(seat => seat.maxAssignableSeats !== null).map(seat => seat.seatName);
};

/**
 * @returns an object storing all seat types and their corresponding total seat counts
 */
export const getAllHubSeatTypesCount = ({
  seatsInventory
}) => {
  const paidSeats = getPaidSeats({
    seatsInventory
  });
  if (!paidSeats.length) return {};
  return paidSeats.reduce((acc, curr) => {
    return Object.assign({}, acc, {
      [curr.seatName]: curr.maxAssignableSeats
    });
  }, {});
};
export const isSeatTypeCore = seatType => seatType === SeatType.CORE;
export const isSeatTypeSales = seatType => seatType === SeatType.SALES_PRO || seatType === SeatType.SALES_ENTERPRISE;
export const isSeatTypeService = seatType => seatType === SeatType.SERVICE_PROFESSIONAL || seatType === SeatType.SERVICE_ENTERPRISE;

/**
 * @returns skuId for the specified seats quantity pack
 * Used to get the skuId for additional seats, which are quantity packs
 * */
const getSkuIdFromBundle = (state, bundle, unitScope) => {
  const seatsQuantityPack = getQuantityPackForProductVersion(state, {
    productVersionSkuId: bundle.skuId,
    unitType: USER,
    unitScope
  });
  return seatsQuantityPack ? seatsQuantityPack.skuId : null;
};

/**
 * @returns skuId for the Core product, depending on the productLevel
 */
const getSkuIdForCoreSeat = (state, productLevel) => {
  const apiName = CoreSeatApiNameByPaidProductLevel[productLevel];
  return getCurrentSkuIdForProduct(state, {
    apiName
  });
};

/**
 * @returns skuId for either the Sales Professional or Sales Enterprise product, depending on the productLevel
 * This should only ever be called if SeatType is professional or enterprise, there is no such thing as starter sales seats
 */
const getSkuIdForSalesSeat = (state, productLevel, ownedBundle) => {
  if (ownedBundle) {
    return getSkuIdFromBundle(state, ownedBundle, SALES);
  }
  const apiName = productLevel === PROFESSIONAL ? SALES_HUB_PROFESSIONAL : SALES_HUB_ENTERPRISE;
  return getCurrentSkuIdForProduct(state, {
    apiName
  });
};

/**
 * @returns skuId for either the Service Professional or Service Enterprise product, depending on the productLevel
 * This should only ever be called if SeatType is professional or enterprise, there is no such thing as starter service seats
 */
const getSkuIdForServiceSeat = (state, productLevel, ownedBundle) => {
  if (ownedBundle) {
    return getSkuIdFromBundle(state, ownedBundle, SERVICE);
  }
  const apiName = productLevel === PROFESSIONAL ? SERVICE_HUB_PROFESSIONAL : SERVICE_HUB_ENTERPRISE;
  return getCurrentSkuIdForProduct(state, {
    apiName
  });
};

/**
 * Sorts seats by type: SALES -> SERVICE -> CORE
 */
const sortSeatsByType = (a, b) => {
  if (isSeatTypeCore(a)) return 1;
  if (isSeatTypeCore(b)) return -1;
  if (isSeatTypeSales(a)) return -1;
  if (isSeatTypeSales(b)) return 1;
  return 0;
};

/**
 * @returns an array of all purchasable seat types and their corresponding skuIds
 * Used to provide all purchasable seat types and their corresponding skuIds to the pricing page (even if the count is 0)
 * E.g. If the users has Sales Hub Pro, and 1 Sales Seat and no Core Seats, the pricing page should still show the option to purchase Core Seats
 * */
export const getAllPurchasableSeatTypesWithSkuIds = state => {
  const {
    seatsInventory
  } = state;
  const paidSeats = getPaidSeats({
    seatsInventory
  });
  if (!paidSeats.length) return [];
  const purchasableSeatTypes = getAllPurchasableHubSeatTypes(state);
  const coreProductLevel = getHighestProductLevelFromProductInventory(state);
  const salesProductLevel = getHighestProductLevelFromProductInventory(state, {
    productLine: SALES
  });
  const serviceProductLevel = getHighestProductLevelFromProductInventory(state, {
    productLine: SERVICE
  });
  const ownedBundle = getProductVersionsFromProductInventory(state).find(productVersion => skuIdHasBundledProducts(state, {
    skuId: productVersion.skuId
  }));
  const skuObjs = purchasableSeatTypes.sort(sortSeatsByType).reduce((acc, curr) => {
    let skuId;
    if (isSeatTypeCore(curr)) {
      skuId = getSkuIdForCoreSeat(state, coreProductLevel);
    } else if (isSeatTypeSales(curr)) {
      skuId = getSkuIdForSalesSeat(state, salesProductLevel, ownedBundle);
    } else if (isSeatTypeService(curr)) {
      skuId = getSkuIdForServiceSeat(state, serviceProductLevel, ownedBundle);
    }
    if (skuId) {
      acc.push({
        seatType: curr,
        skuId
      });
    }
    return acc;
  }, []);
  return skuObjs;
};
export const getHasSalesAndServiceSeats = state => {
  return getTotalHubSeatsCountForSeatType(state, SeatType.SALES_PRO) > 0 && getTotalHubSeatsCountForSeatType(state, SeatType.SERVICE_PROFESSIONAL) > 0;
};